<script lang="ts" setup>
import { nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useThrottleFn } from '@vueuse/core'
import { useRouter } from 'vue-router/composables'

interface Tag {
  id?: string
  name: string
}

const props = withDefaults(
  defineProps<{
    tags: Tag[]
    center?: boolean
    wrapStyle?: Record<string, string | number>
    itemStyle?: Record<string, string | number>
  }>()
  , {
    center: false,
    wrapStyle: () => ({}),
    itemStyle: () => ({}),
  },
)

const router = useRouter()

const visibleTags = ref<Tag[]>([])
const showEllipsis = ref(false)
const tagsContainer = ref<HTMLElement | null>(null)

// 计算可见标签
function calculateVisibleTags() {
  if (!props.tags?.length) {
    visibleTags.value = []
    showEllipsis.value = false
    return
  }

  // 重置状态
  visibleTags.value = [...props.tags]
  showEllipsis.value = false

  nextTick(() => {
    const container = tagsContainer.value
    if (!container)
      return

    // 获取容器宽度
    const containerWidth = container.clientWidth

    // 获取所有标签元素
    const tagElements = container.querySelectorAll('.tag-item')
    const ellipsisElement = container.querySelector('.ellipsis-tag')

    // 计算省略标签宽度
    const ellipsisWidth = ellipsisElement
      ? ellipsisElement.scrollWidth
      : 30 // 预估宽度

    let totalWidth = 0
    let visibleCount = 0

    // 计算可见标签数量
    for (let i = 0; i < tagElements.length; i++) {
      const tagElement = tagElements[i] as HTMLElement
      const tagWidth = tagElement.scrollWidth
      const marginRight = 5 // 对应 space-x-5px 的间距
      const nextWidth = totalWidth + tagWidth + (i > 0 ? marginRight : 0)

      // 检查是否需要显示省略号
      if (nextWidth + ellipsisWidth > containerWidth) {
        showEllipsis.value = true
        break
      }

      totalWidth = nextWidth
      visibleCount++
    }

    // 更新可见标签
    visibleTags.value = (props.tags || []).slice(0, visibleCount)
  })
}

// 创建节流函数 (500ms)
const throttledCalculate = useThrottleFn(calculateVisibleTags, 500)

// 监听标签列表变化
watch(() => props.tags, calculateVisibleTags, { deep: true })

// 生命周期钩子
onMounted(() => {
  calculateVisibleTags()
  window.addEventListener('resize', throttledCalculate)
})

onBeforeUnmount(() => {
  window.removeEventListener('resize', throttledCalculate)
})

// 点击标签跳转到标签页面
function handleTagClick(tag: Tag, event: Event) {
  event.stopPropagation()
  if (tag.id)
    router.push({ name: 'tags', query: { tagId: tag.id } })
}
</script>

<template>
  <div v-if="tags?.length" class="flex h-18px overflow-hidden" :style="wrapStyle">
    <div
      ref="tagsContainer"
      class="flex w-full overflow-hidden"
      :class="{ 'justify-center': center }"
    >
      <div
        v-for="(tag, tagIndex) in visibleTags"
        :key="tagIndex"
        class="tag-item px-6px text-12px flex-shrink-0 text-n-text-common-tag bg-n-bg-common-tag rounded-14px flex items-center justify-center h-full"
        :class="{ 'mr-5px': tagIndex < visibleTags.length - 1 || showEllipsis, 'cursor-pointer': tag.id }"
        :style="itemStyle"
        @click="handleTagClick(tag, $event)"
      >
        {{ tag.name }}
      </div>

      <div
        v-if="showEllipsis"
        class="ellipsis-tag h-full px-8px text-12px flex-shrink-0 text-n-text-common-tag bg-n-bg-common-tag rounded-14px flex items-center justify-center"
        :style="{ lineHeight: '18px', ...itemStyle }"
      >
        <span class="relative top-[-2px]">...</span>
      </div>
    </div>
  </div>
</template>
